"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.teamsGraphClient = exports.TeamsGraphClient = exports.TEAMS_GRAPH_API_NAMES = void 0;
const tslib_1 = require("tslib");
const hooks_1 = require("@feathersjs/hooks");
const constants_1 = require("../common/constants");
const globalVars_1 = require("../common/globalVars");
const wrappedAxiosClient_1 = require("../common/wrappedAxiosClient");
const teamsGraph_1 = require("../error/teamsGraph");
const retryHandler_1 = require("../common/retryHandler");
class TEAMS_GRAPH_API_NAMES {
    static GET_OAUTH = "teams_graph_get_oauth";
    static CREATE_OAUTH = "teams_graph_create_oauth";
    static UPDATE_OAUTH = "teams_graph_update_oauth";
    static GET_API_KEY = "teams_graph_get_api_key";
    static CREATE_API_KEY = "teams_graph_create_api_key";
    static UPDATE_API_KEY = "teams_graph_update_api_key";
}
exports.TEAMS_GRAPH_API_NAMES = TEAMS_GRAPH_API_NAMES;
class TeamsGraphClient {
    getEndpoint() {
        return (0, constants_1.getResourceServiceEndpoint)(constants_1.ResourceServiceType.TeamsGraph);
    }
    createRequesterWithToken(token) {
        const instance = wrappedAxiosClient_1.WrappedAxiosClient.create({
            baseURL: this.getEndpoint(),
        });
        instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        instance.defaults.headers.common["Client-Source"] = "agentstoolkit";
        return instance;
    }
    wrapException(e, apiName) {
        const headers = e.response?.headers;
        const correlationId = headers?.["x-correlation-id"] ?? headers?.["request-id"] ?? headers?.["x-ms-request-id"];
        let extraData = e.response?.data ? `data: ${JSON.stringify(e.response.data)}` : "";
        if (!e.message?.toLowerCase().includes("status code") && e.response?.status) {
            extraData = `Status code: ${e.response.status}. ${extraData}`;
        }
        return new teamsGraph_1.TeamsGraphAPIFailedSystemError(e, correlationId, apiName, extraData);
    }
    async getOauthRegistrationById(token, oauthRegistrationId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/v1.0/oAuthConfigurations/${oauthRegistrationId}`));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, TEAMS_GRAPH_API_NAMES.GET_OAUTH);
        }
    }
    async createOauthRegistration(token, oauthRegistration) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post("/v1.0/oAuthConfigurations", oauthRegistration));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, TEAMS_GRAPH_API_NAMES.CREATE_OAUTH);
        }
    }
    async updateOauthRegistration(token, oauthRegistration, oauthRegistrationId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.patch(`/v1.0/oAuthConfigurations/${oauthRegistrationId}`, oauthRegistration));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, TEAMS_GRAPH_API_NAMES.UPDATE_OAUTH);
        }
    }
    async getApiKeyRegistrationById(token, apiKeyRegistrationId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/v1.0/apiSecretRegistrations/${apiKeyRegistrationId}`));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, TEAMS_GRAPH_API_NAMES.GET_API_KEY);
        }
    }
    async createApiKeyRegistration(token, apiKeyRegistration) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post("/v1.0/apiSecretRegistrations", apiKeyRegistration));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, TEAMS_GRAPH_API_NAMES.CREATE_API_KEY);
        }
    }
    async updateApiKeyRegistration(token, apiKeyRegistration, apiKeyRegistrationId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.patch(`/v1.0/apiSecretRegistrations/${apiKeyRegistrationId}`, apiKeyRegistration));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, TEAMS_GRAPH_API_NAMES.UPDATE_API_KEY);
        }
    }
}
exports.TeamsGraphClient = TeamsGraphClient;
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "TeamsGraph", component: "TeamsGraphClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsGraphClient.prototype, "getOauthRegistrationById", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "TeamsGraph", component: "TeamsGraphClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsGraphClient.prototype, "createOauthRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "TeamsGraph", component: "TeamsGraphClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsGraphClient.prototype, "updateOauthRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "TeamsGraph", component: "TeamsGraphClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsGraphClient.prototype, "getApiKeyRegistrationById", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "TeamsGraph", component: "TeamsGraphClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsGraphClient.prototype, "createApiKeyRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "TeamsGraph", component: "TeamsGraphClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsGraphClient.prototype, "updateApiKeyRegistration", null);
exports.teamsGraphClient = new TeamsGraphClient();
//# sourceMappingURL=teamsGraphClient.js.map